import { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import StickyCTABar from "@/components/site/StickyCTABar";
import CookieConsent from "@/components/site/CookieConsent";
import JsonLd from "@/components/site/JsonLd";
import { ARTICLES } from "@/config/knowledgeBase";
import { trackEvent, logClick, ctaLocationOf } from "@/lib/tracking";
import { initAttribution } from "@/lib/attribution";
import { getLeadRef } from "@/lib/leadRef";
import { withLeadRef } from "@/config/contact";
import { PLUMBER_SCHEMA } from "@/lib/schema";

// Code splitting per route: de eerste lading bevat alleen de huidige pagina.
const Toaster = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

// Route-loaders als één bron van waarheid. index.js laadt de chunk van de huidige
// route vóór hydrateRoot, zodat hydratie van de geprerenderde HTML zonder
// Suspense-fallback-mismatch verloopt (een geladen lazy resolveert direct).
const routeLoaders = {
  "/": () => import("@/pages/HomePage"),
  "/bedankt": () => import("@/pages/ThankYouPage"),
  "/privacyverklaring": () => import("@/pages/PrivacyPage"),
  "/algemene-voorwaarden": () => import("@/pages/TermsPage"),
  "/over-ons": () => import("@/pages/AboutPage"),
  "/kennisbank": () => import("@/pages/KnowledgeIndex"),
  "/werkgebied": () => import("@/pages/WorkAreaPage"),
  kennisbankArticle: () => import("@/pages/KnowledgeArticle"),
  landing: () => import("@/pages/LandingPage"),
  notFound: () => import("@/pages/NotFound"),
};
const LANDING_SLUGS = ["/lekdetectie", "/daklekkage", "/leidinglekkage", "/cv-lekkage", "/lekkage-badkamer", "/waterschade", "/lekkage-verzekering", "/lekkage-vve", "/lekkage-amsterdam", "/lekkage-utrecht", "/lekkage-almere", "/lekkage-amstelveen", "/lekkage-zaandam", "/lekkage-amersfoort", "/lekkage-hilversum", "/lekkage-haarlem"];

export const preloadRoute = (pathname) => {
  const clean = pathname.replace(/\/+$/, "") || "/";
  const loader =
    routeLoaders[clean] ||
    (clean.startsWith("/kennisbank/") ? routeLoaders.kennisbankArticle : null) ||
    (LANDING_SLUGS.includes(clean) ? routeLoaders.landing : null) ||
    routeLoaders.notFound;
  return loader().catch(() => {});
};

const HomePage = lazy(routeLoaders["/"]);
const ThankYouPage = lazy(routeLoaders["/bedankt"]);
const PrivacyPage = lazy(routeLoaders["/privacyverklaring"]);
const TermsPage = lazy(routeLoaders["/algemene-voorwaarden"]);
const NotFound = lazy(routeLoaders.notFound);
const LandingPage = lazy(routeLoaders.landing);
const AboutPage = lazy(routeLoaders["/over-ons"]);
const KnowledgeIndex = lazy(routeLoaders["/kennisbank"]);
const WorkAreaPage = lazy(routeLoaders["/werkgebied"]);
const KnowledgeArticle = lazy(routeLoaders.kennisbankArticle);

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Sonner mount zijn <section> zodra de chunk geladen is. Tijdens prerender staat die
// dus wél in de HTML, terwijl de client bij hydratie (chunk nog niet geladen) op de
// Suspense-fallback null uitkomt → mismatch (#418). Daarom pas renderen ná mount, en
// tijdens prerender helemaal niet: server-HTML en eerste client-render zijn dan beide
// leeg, de toaster verschijnt pas na hydratie.
const DeferredToaster = () => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    if (!window.__PRERENDER__) setMounted(true);
  }, []);
  if (!mounted) return null;
  return (
    <Suspense fallback={null}>
      <Toaster position="top-center" theme="dark" richColors />
    </Suspense>
  );
};

// link_location volgens meetplan: header | hero | sticky | cta | footer
const linkLocationOf = (el) => {
  const loc = ctaLocationOf(el);
  return { header: "header", hero: "hero", sticky_mobile: "sticky", footer: "footer" }[loc] || "cta";
};

function App() {
  useEffect(() => {
    getLeadRef();
    initAttribution();
    // Lenis pas na mount laden: houdt de eerste JS-lading klein, scroll-gedrag blijft gelijk.
    let lenis = null;
    let frame;
    let cancelled = false;
    import("lenis").then(({ default: Lenis }) => {
      if (cancelled) return;
      lenis = new Lenis({ duration: 1.2, smoothWheel: true });
      const raf = (time) => {
        lenis.raf(time);
        frame = requestAnimationFrame(raf);
      };
      frame = requestAnimationFrame(raf);
    });
    const onClick = (e) => {
      const anchor = e.target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href") || "";
      if (href.startsWith("tel:")) {
        const ctaId = anchor.getAttribute("data-testid") || "unknown";
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "lead_call_click", link_location: linkLocationOf(anchor), page_path: window.location.pathname });
        trackEvent("phone_click", { cta_location: ctaLocationOf(anchor), cta_id: ctaId, link_url: href });
        logClick("phone_click", ctaId);
      } else if (href.includes("wa.me")) {
        // lead_ref pas bij de klik aan de href toevoegen: de gerenderde HTML blijft
        // daardoor identiek tussen prerender en client (geen hydratie-mismatch).
        anchor.href = withLeadRef(href);
        const ctaId = anchor.getAttribute("data-testid") || "unknown";
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: "lead_whatsapp_click", link_location: linkLocationOf(anchor), page_path: window.location.pathname });
        trackEvent("whatsapp_click", { cta_location: ctaLocationOf(anchor), cta_id: ctaId, link_url: href.split("?")[0] });
        logClick("whatsapp_click", ctaId);
      } else if (href.startsWith("mailto:")) {
        trackEvent("email_click", { cta_location: ctaLocationOf(anchor), cta_id: anchor.getAttribute("data-testid") || "unknown", link_url: href });
      } else if (href.startsWith("#")) {
        const el = document.querySelector(href);
        if (el) {
          e.preventDefault();
          if (lenis) lenis.scrollTo(el, { offset: -80 });
          else el.scrollIntoView({ behavior: "smooth" });
        }
      }
    };
    document.addEventListener("click", onClick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      document.removeEventListener("click", onClick);
      lenis?.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <div className="bg-[#0B1320] text-gray-100 min-h-screen antialiased pb-[calc(72px+env(safe-area-inset-bottom,0px))] md:pb-0 overflow-x-clip">
        <DeferredToaster />
        <JsonLd data={PLUMBER_SCHEMA} />
        <Header />
        <main>
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/bedankt" element={<ThankYouPage />} />
              <Route path="/privacyverklaring" element={<PrivacyPage />} />
              <Route path="/algemene-voorwaarden" element={<TermsPage />} />
              <Route path="/over-ons" element={<AboutPage />} />
              <Route path="/404" element={<NotFound />} />
              <Route path="/kennisbank" element={<KnowledgeIndex />} />
              <Route path="/werkgebied" element={<WorkAreaPage />} />
              {Object.values(ARTICLES).map((article) => (
                <Route key={article.slug} path={`/kennisbank/${article.slug}`} element={<KnowledgeArticle article={article} />} />
              ))}
              {/* Statische routes i.p.v. import van landingPages.js: zo blijft die 52KB-config uit de eerste lading */}
              <Route path="/lekdetectie" element={<LandingPage />} />
              <Route path="/daklekkage" element={<LandingPage />} />
              <Route path="/leidinglekkage" element={<LandingPage />} />
              <Route path="/cv-lekkage" element={<LandingPage />} />
              <Route path="/lekkage-badkamer" element={<LandingPage />} />
              <Route path="/waterschade" element={<LandingPage />} />
              <Route path="/lekkage-verzekering" element={<LandingPage />} />
              <Route path="/lekkage-vve" element={<LandingPage />} />
              <Route path="/lekkage-amsterdam" element={<LandingPage />} />
              <Route path="/lekkage-utrecht" element={<LandingPage />} />
              <Route path="/lekkage-almere" element={<LandingPage />} />
              <Route path="/lekkage-amstelveen" element={<LandingPage />} />
              <Route path="/lekkage-zaandam" element={<LandingPage />} />
              <Route path="/lekkage-amersfoort" element={<LandingPage />} />
              <Route path="/lekkage-hilversum" element={<LandingPage />} />
              <Route path="/lekkage-haarlem" element={<LandingPage />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
        <StickyCTABar />
        <CookieConsent />
      </div>
    </BrowserRouter>
  );
}

export default App;
